import { 
  collection, 
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getErrorMessage, validateRequired, checkConnection } from './errorHandler';

const COLLECTION = 'appointments';

/**
 * Criar novo agendamento
 * Suporta agendamentos com múltiplos slots (para serviços longos)
 */
export const createAppointment = async (appointmentData) => {
  try {
    // Validar conexão
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    // Validar campos obrigatórios
    validateRequired(
      [appointmentData.barbershopId, appointmentData.userId, appointmentData.date, appointmentData.time, appointmentData.service],
      ['Barbearia', 'Usuário', 'Data', 'Horário', 'Serviço']
    );

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...appointmentData,
      status: 'pending', // pending, confirmed, completed, cancelled, no-show
      // Armazena array de slot IDs se o agendamento ocupa múltiplos horários
      slotIds: appointmentData.slotIds || (appointmentData.slotId ? [appointmentData.slotId] : []),
      totalDuration: appointmentData.totalDuration || 30, // duração total em minutos
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating appointment:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Buscar agendamento por ID
 */
export const getAppointmentById = async (appointmentId) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    if (!appointmentId) {
      return { success: false, error: 'ID do agendamento não fornecido' };
    }

    const docRef = doc(db, COLLECTION, appointmentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: 'Agendamento não encontrado' };
  } catch (error) {
    console.error('Error getting appointment:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Buscar agendamentos de um usuário
 */
export const getAppointmentsByUserId = async (userId) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    if (!userId) {
      return { success: false, error: 'ID do usuário não fornecido' };
    }

    const q = query(
      collection(db, COLLECTION), 
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const appointments = [];
    
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: appointments };
  } catch (error) {
    console.error('Error getting appointments by user:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Buscar agendamentos de uma barbearia
 */
export const getAppointmentsByBarbershopId = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('barbershopId', '==', barbershopId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const appointments = [];
    
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: appointments };
  } catch (error) {
    console.error('Error getting appointments by barbershop:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar agendamentos pendentes de uma barbearia
 */
export const getPendingAppointmentsByBarbershopId = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('barbershopId', '==', barbershopId),
      where('status', '==', 'pending'),
      orderBy('date', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const appointments = [];
    
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: appointments };
  } catch (error) {
    console.error('Error getting pending appointments:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar status do agendamento
 */
export const updateAppointmentStatus = async (appointmentId, status, extraData = {}) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    validateRequired([appointmentId, status], ['ID do agendamento', 'Status']);

    const validStatuses = ['pending', 'confirmed', 'completed', 'cancelled', 'no-show', 'expired'];
    if (!validStatuses.includes(status)) {
      return { success: false, error: 'Status inválido' };
    }

    const docRef = doc(db, COLLECTION, appointmentId);
    await updateDoc(docRef, {
      status,
      ...extraData,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating appointment status:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Atualizar agendamento
 */
export const updateAppointment = async (appointmentId, updates) => {
  try {
    const docRef = doc(db, COLLECTION, appointmentId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating appointment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancelar agendamento
 */
export const cancelAppointment = async (appointmentId) => {
  return updateAppointmentStatus(appointmentId, 'cancelled');
};

/**
 * Cancelar agendamento com motivo
 */
export const cancelAppointmentWithReason = async (appointmentId, reason) => {
  try {
    if (!checkConnection()) {
      return { success: false, error: 'Sem conexão com a internet' };
    }

    if (!appointmentId) {
      return { success: false, error: 'ID do agendamento não fornecido' };
    }

    const docRef = doc(db, COLLECTION, appointmentId);
    await updateDoc(docRef, {
      status: 'cancelled',
      cancellationReason: reason || '',
      cancelledAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

/**
 * Verificar se o cancelamento é permitido (4h de antecedência)
 */
export const canCancelAppointment = (appointmentDate, appointmentTime) => {
  const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}`);
  const now = new Date();
  const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60); // Converter para horas
  
  return {
    canCancel: hoursUntilAppointment >= 4,
    hoursUntilAppointment: Math.max(0, hoursUntilAppointment)
  };
};

/**
 * Deletar agendamento
 */
export const deleteAppointment = async (appointmentId) => {
  try {
    const docRef = doc(db, COLLECTION, appointmentId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting appointment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listener em tempo real para agendamentos de um usuário
 */
export const subscribeToUserAppointments = (userId, callback) => {
  const q = query(
    collection(db, COLLECTION), 
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const appointments = [];
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    callback(appointments);
  }, (error) => {
    console.error('Error in appointments listener:', error);
    callback([]);
  });
};

/**
 * Listener em tempo real para agendamentos de uma barbearia
 */
export const subscribeToBarbershopAppointments = (barbershopId, callback) => {
  const q = query(
    collection(db, COLLECTION), 
    where('barbershopId', '==', barbershopId),
    orderBy('date', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const appointments = [];
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    callback(appointments);
  }, (error) => {
    console.error('Error in barbershop appointments listener:', error);
    callback([]);
  });
};
